/**
 * How a service call bills (F6, F8, D86) — the phone's copy of
 * `app/services/service_call.py`, to the cent, checked against the shared
 * cases in `tests/fixtures/costing_cases.json`.
 *
 * A fee covers the first minutes on site and the first km; labour after that
 * is per 15 minutes to the NEAREST block (Aaron's rule: 8 over is one block, 7
 * is none); travel is the round trip less the included km; a contractor
 * customer's own rates win where set. Quoted jobs don't use any of it.
 */

import { cents } from "./costing";

export const BLOCK_MINUTES = 15;

export type Rates = {
  fee_cents: number;
  minutes_included: number;
  hourly_cents: number;
  km_included: number;
  km_rate_cents: number;
};

export type ChargeLine = { key: "fee" | "labour" | "travel"; description: string; qty: number; unit_cents: number };

/** Round half up, the way Python's `Decimal.quantize(ROUND_HALF_UP)` does for positives. */
function halfUp(value: number): number {
  return Math.floor(value + 0.5 + 1e-9);
}

export function labourBlocks(minutesOnSite: number, minutesIncluded: number): number {
  const over = Math.max(0, Math.trunc(minutesOnSite) - Math.trunc(minutesIncluded));
  return halfUp(over / BLOCK_MINUTES);
}

export function travelKm(oneWayKm: number | null | undefined, kmIncluded: number): number {
  if (oneWayKm === null || oneWayKm === undefined || oneWayKm <= 0) return 0;
  const charged = oneWayKm * 2 - kmIncluded;
  return Math.max(0, halfUp(charged * 10) / 10);
}

function kmText(km: number): string {
  return km.toFixed(1).replace(/\.0$/, "");
}

/**
 * `minutesOnSite` is the first tech's; `extraMinutes` everyone else's, none
 * of it covered by the fee (CLOCK-3: Aaron's rule).
 */
export function charge(
  minutesOnSite: number,
  oneWayKm: number | null | undefined,
  rates: Rates,
  { extraMinutes = 0, techs = 1 }: { extraMinutes?: number; techs?: number } = {},
): ChargeLine[] {
  const lines: ChargeLine[] = [
    {
      key: "fee",
      description: `Service call — first ${rates.minutes_included} min and ${rates.km_included} km`,
      qty: 1,
      unit_cents: rates.fee_cents,
    },
  ];
  const blocks = labourBlocks(Math.max(0, minutesOnSite - rates.minutes_included) + Math.max(0, extraMinutes), 0);
  if (blocks) {
    lines.push({
      key: "labour",
      description: `Labour — ${blocks} × 15 min` + (techs > 1 ? ` (${techs} techs)` : ""),
      qty: blocks,
      unit_cents: halfUp(rates.hourly_cents / 4),
    });
  }
  const km = travelKm(oneWayKm, rates.km_included);
  if (km > 0) {
    lines.push({
      key: "travel",
      description: `Travel — ${kmText(km)} km beyond the first ${rates.km_included}`,
      qty: km,
      unit_cents: rates.km_rate_cents,
    });
  }
  return lines;
}

export function lineTotal(line: ChargeLine): number {
  return cents(line.qty * line.unit_cents);
}

export function totalCents(lines: ChargeLine[]): number {
  return lines.reduce((sum, line) => sum + lineTotal(line), 0);
}

type Row = Record<string, unknown>;
const n = (v: unknown, fallback = 0): number => (v === null || v === undefined || v === "" ? fallback : Number(v));
const maybe = (v: unknown): number | null => (v === null || v === undefined || v === "" ? null : Number(v));

/** The business's rates, or a contractor customer's where set. Mirrors `service_call.rates_for`. */
export function ratesFor(org: Row, customer: Row | null, contractor: boolean): Rates {
  const pick = (own: unknown, fallback: unknown) => (contractor && maybe(own) !== null ? n(own) : n(fallback));
  return {
    fee_cents: pick(customer?.contractor_fee_cents, org.service_call_fee_cents),
    minutes_included: n(org.service_call_minutes_included, 30),
    hourly_cents: pick(customer?.contractor_hourly_rate_cents, org.service_hourly_rate_cents),
    km_included: n(org.service_km_included, 50),
    km_rate_cents: pick(customer?.contractor_km_rate_cents, org.service_km_rate_cents),
  };
}

export function applies(job: { quoted_cents?: number | null; billing_mode?: string | null }): boolean {
  return !job.quoted_cents && (job.billing_mode === "service_call" || job.billing_mode === "contractor");
}

type Clocked = { kind?: string | null; billable?: boolean; minutes?: number; user_id?: string | null; started_at?: string | null };

/**
 * (first tech's minutes, everyone else's, how many techs) — CLOCK-3. The first
 * tech is whoever started on site first; ties go to whoever has more minutes.
 * Mirrors `service_call.split_by_tech`.
 */
export function splitByTech(entries: Clocked[]): [number, number, number] {
  const firstSeen = new Map<string, string>();
  const minutes = new Map<string, number>();
  for (const e of entries) {
    if (e.billable === false || (e.kind ?? "on_site") !== "on_site") continue;
    const who = String(e.user_id ?? "");
    const mark = e.started_at ? new Date(e.started_at).toISOString() : "~";
    const seen = firstSeen.get(who);
    firstSeen.set(who, seen === undefined || mark < seen ? mark : seen);
    minutes.set(who, (minutes.get(who) ?? 0) + n(e.minutes));
  }
  if (minutes.size === 0) return [0, 0, 0];
  let lead = "";
  for (const who of minutes.keys()) {
    if (
      lead === "" ||
      (firstSeen.get(who) as string) < (firstSeen.get(lead) as string) ||
      (firstSeen.get(who) === firstSeen.get(lead) && (minutes.get(who) ?? 0) > (minutes.get(lead) ?? 0))
    ) {
      lead = who;
    }
  }
  const total = [...minutes.values()].reduce((a, b) => a + b, 0);
  const first = minutes.get(lead) ?? 0;
  return [first, total - first, minutes.size];
}

/** Minutes that count for the 15-minute rule: billable on-site time only. */
export function minutesOnSite(entries: { kind?: string | null; billable?: boolean; minutes?: number }[]): number {
  return entries
    .filter((e) => e.billable !== false && (e.kind ?? "on_site") === "on_site")
    .reduce((sum, e) => sum + n(e.minutes), 0);
}

export function oneWayKm(job: Row, site: Row | null): number | null {
  const own = maybe(job.travel_km_one_way);
  if (own !== null) return own;
  return site ? maybe(site.distance_km) : null;
}

/**
 * The service-call lines for a job from what the device holds, or null when
 * it doesn't bill that way. Not set up (no fee and no hourly rate) is an empty
 * list here — the office's invoice is what refuses, with the reason.
 */
export function forJob(
  job: Row & { quoted_cents?: number | null; billing_mode?: string | null },
  entries: Clocked[],
  org: Row | null,
  customer: Row | null,
  site: Row | null,
): ChargeLine[] | null {
  if (!applies(job)) return null;
  // The server's answer for whose rates, when the job carries it: an office
  // device may not hold a contractor's customer row, and falling back to the
  // business's rates there showed the wrong money (D86).
  const served = (job.service as { rates?: Rates } | null | undefined)?.rates;
  if (!served && !org) return null;
  const rates = served ?? ratesFor(org as Row, customer, job.billing_mode === "contractor");
  if (rates.fee_cents <= 0 && rates.hourly_cents <= 0) return [];
  const [first, others, techs] = splitByTech(entries);
  return charge(first, oneWayKm(job, site), rates, { extraMinutes: others, techs });
}
